import React, { useState, useEffect } from 'react';
import './CustomCalendar.css';
import { FaChevronLeft, FaChevronRight, FaRegCalendarAlt } from 'react-icons/fa';
import { getAvailableBedCount } from '../utils/bedAvailability';

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay();
}

// Get available beds for a specific date
async function getBedsForDate(date, month, year) {
  try {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
    const nextDay = new Date(year, month, date + 1);
    const nextDateStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
    
    const availableBeds = await getAvailableBedCount(dateStr, nextDateStr);
    return availableBeds;
  } catch (error) {
    console.error('Error getting bed count for date:', error);
    return 38; // Return total beds on error
  }
}

export default function CustomCalendar({ selectedRange, onSelectRange, isHourlyPackage = false }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [internalRange, setInternalRange] = useState([null, null]);
  const [rangeMeta, setRangeMeta] = useState({ startMonth: null, startYear: null, endMonth: null, endYear: null });
  const [bedCounts, setBedCounts] = useState({});

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);

  // Load bed counts for current month
  useEffect(() => {
    const loadBedCounts = async () => {
      const counts = {};
      for (let day = 1; day <= daysInMonth; day++) {
        const bedCount = await getBedsForDate(day, viewMonth, viewYear);
        counts[day] = bedCount;
      }
      setBedCounts(counts);
    };

    loadBedCounts();
  }, [viewYear, viewMonth, daysInMonth]);

  // Build calendar grid (6 rows x 7 days)
  const calendarCells = [];
  let dayNum = 1;
  for (let row = 0; row < 6; row++) {
    const week = [];
    for (let col = 0; col < 7; col++) {
      if ((row === 0 && col < firstDay) || dayNum > daysInMonth) {
        week.push(null);
      } else {
        week.push(dayNum);
        dayNum++;
      }
    }
    calendarCells.push(week);
  }

  // Range selection logic
  const range = selectedRange || internalRange;
  const [start, end] = range;
  function handleDayClick(date) {
    let meta = {};
    if (isHourlyPackage) {
      // Single-day selection for hourly packages
      setInternalRange([date, date]);
      meta = { startMonth: viewMonth, startYear: viewYear, endMonth: viewMonth, endYear: viewYear };
      setRangeMeta(meta);
      if (onSelectRange) onSelectRange([date, date], meta);
      return;
    }

    // Non-hourly range selection with cross-month support
    if (!start || (start && end)) {
      // Start a new selection
      const newMeta = { startMonth: viewMonth, startYear: viewYear, endMonth: null, endYear: null };
      setInternalRange([date, null]);
      setRangeMeta(newMeta);
      if (onSelectRange) onSelectRange([date, null], newMeta);
      return;
    }

    // We have a start day but no end yet - compare full dates across months/years
    const startFull = new Date(rangeMeta.startYear ?? viewYear, rangeMeta.startMonth ?? viewMonth, start);
    const clickedFull = new Date(viewYear, viewMonth, date);

    if (clickedFull < startFull) {
      // If clicked date is before the start, flip the range
      const newMeta = { startMonth: viewMonth, startYear: viewYear, endMonth: rangeMeta.startMonth ?? viewMonth, endYear: rangeMeta.startYear ?? viewYear };
      setInternalRange([date, start]);
      setRangeMeta(newMeta);
      if (onSelectRange) onSelectRange([date, start], newMeta);
    } else {
      // Normal end selection
      const newMeta = { startMonth: rangeMeta.startMonth ?? viewMonth, startYear: rangeMeta.startYear ?? viewYear, endMonth: viewMonth, endYear: viewYear };
      setInternalRange([start, date]);
      setRangeMeta(newMeta);
      if (onSelectRange) onSelectRange([start, date], newMeta);
    }
  }

  function handlePrevMonth() {
    // Do not reset selection; allow selecting end date across months
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(y => y - 1);
    } else {
      setViewMonth(m => m - 1);
    }
  }
  function handleNextMonth() {
    // Do not reset selection; allow selecting end date across months
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(y => y + 1);
    } else {
      setViewMonth(m => m + 1);
    }
  }

  return (
    <div className="custom-calendar">
      <div className="calendar-header">
        <FaRegCalendarAlt className="calendar-icon" />
        <span className="calendar-month">{new Date(viewYear, viewMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
        <div className="calendar-nav">
          <button onClick={handlePrevMonth}><FaChevronLeft /></button>
          <button onClick={handleNextMonth}><FaChevronRight /></button>
        </div>
      </div>
      <div className="calendar-grid">
        <div className="calendar-weekdays">
          {WEEK_DAYS.map(day => (
            <div key={day} className="calendar-weekday">{day}</div>
          ))}
        </div>
        {calendarCells.map((week, i) => (
          <div className="calendar-week" key={i}>
            {week.map((date, j) => {
              if (!date) return <div key={j} className="calendar-day empty"></div>;
              // Calculate the real date for this cell
              const cellDate = new Date(viewYear, viewMonth, date);
              const isPast = cellDate.setHours(0,0,0,0) < (new Date().setHours(0,0,0,0));
              return (
                <div
                  key={j}
                  className={`calendar-day${
                    isPast ? ' disabled' : ''
                  }${
                    start && end && date >= start && date <= end ? ' selected' :
                    start && !end && date === start ? ' selected' : ''
                  }`}
                  onClick={!isPast ? () => handleDayClick(date) : undefined}
                  style={isPast ? { pointerEvents: 'none', opacity: 0.4, background: '#f4f4f4' } : {}}
                >
                  <span className="calendar-date">{date}</span>
                  <span className="calendar-beds">{bedCounts[date] || 0} beds</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="calendar-legend">
        <span className="legend-box selected"></span> Selected
      </div>
    </div>
  );
}
